import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const AuditForm = ({ selectedDay, selectedTime }) => {
    const { t } = useTranslation();
    const [forms, setForms] = useState({
        name: '',
        email: '',
        phone: '',
        website: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const API_URL = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + 'audits.php' : 'https://orbitronix.ge/api/audits.php';
    const RECAPTCHA_SITE_KEY = '6LcUPFEtAAAAAIa-1gqL2ziVSccxdhdZu40VJjM3';

    useEffect(() => {
        const scriptId = 'recaptcha-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://www.google.com/recaptcha/api.js';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }
    }, []);

    const changeHandler = e => {
        setForms({ ...forms, [e.target.name]: e.target.value });
        setError('');
    };

    const validateForm = () => {
        if (!forms.name.trim()) {
            setError(t('audit.errors.nameRequired'));
            return false;
        }
        if (!forms.email.trim()) {
            setError(t('audit.errors.emailRequired'));
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(forms.email)) {
            setError(t('audit.errors.emailInvalid'));
            return false;
        }
        if (!forms.phone.trim()) {
            setError(t('audit.errors.phoneRequired'));
            return false;
        }
        if (!forms.website.trim()) {
            setError(t('audit.errors.websiteRequired'));
            return false;
        }
        const recaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();
        if (!recaptchaResponse) {
            setError(t('audit.errors.recaptchaRequired'));
            return false;
        }
        return true;
    };

    const submitHandler = async e => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setError('');

        try {
            const recaptchaResponse = window.grecaptcha && window.grecaptcha.getResponse();
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...forms, preferred_day: selectedDay, preferred_time: selectedTime, recaptcha_response: recaptchaResponse })
            });

            const result = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                setForms({ name: '', email: '', phone: '', website: '', message: '' });
                if (window.grecaptcha) {
                    window.grecaptcha.reset();
                }
                setTimeout(() => setIsSubmitted(false), 5000);
            } else {
                throw new Error(result.error || t('audit.errors.submissionFailed'));
            }
        } catch (err) {
            setError(t('audit.errors.submissionFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={submitHandler} className="contact-form">
            <div className="row">
                <div className="col-lg-6">
                    <div className="input-field">
                        <label>{t('contactForm.name')}</label>
                        <div className="input-box">
                            <input
                                value={forms.name}
                                type="text"
                                name="name"
                                className="form-control"
                                placeholder={t('contactForm.name')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="input-field">
                        <label>{t('contactForm.email')}</label>
                        <div className="input-box">
                            <input
                                value={forms.email}
                                type="email"
                                name="email"
                                className="form-control"
                                placeholder={t('contactForm.email')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="input-field">
                        <label>{t('contactForm.phone')}</label>
                        <div className="input-box">
                            <input
                                value={forms.phone}
                                type="tel"
                                name="phone"
                                className="form-control"
                                placeholder={t('contactForm.phone')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="input-field">
                        <label>{t('audit.websiteUrl')}</label>
                        <div className="input-box">
                            <input
                                value={forms.website}
                                type="url"
                                name="website"
                                className="form-control"
                                placeholder={t('audit.websitePlaceholder')}
                                onChange={changeHandler}
                                required
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-12">
                    <div className="input-field text-field">
                        <label>{t('contactForm.message')}</label>
                        <div className="input-box">
                            <textarea
                                onChange={changeHandler}
                                value={forms.message}
                                name="message"
                                className="form-control"
                                placeholder={t('audit.messagePlaceholder')}
                                rows="4"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-20">
                <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} data-theme="dark"></div>
            </div>

            {error && (
                <div className="error-message mt-20" style={{
                    color: '#e74c3c',
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    borderRadius: '4px',
                    border: '1px solid #f5c6cb'
                }}>
                    {error}
                </div>
            )}

            {isSubmitted && (
                <div className="success-message mt-20" style={{
                    color: '#155724',
                    padding: '10px',
                    backgroundColor: '#d4edda',
                    borderRadius: '4px',
                    border: '1px solid #c3e6cb'
                }}>
                    {t('audit.success')}
                </div>
            )}

            <div className="cp-det-btn mt-20 d-grid">
                <button className="cp-btn" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('audit.submitting') : t('audit.submit')}
                    <i className="fal fa-arrow-right"></i>
                </button>
            </div>
        </form>
    );
};

export default AuditForm;
